import type { Store } from "vuex";
import { useStore } from "vuex";

import type { Invoice } from "@/typings/invoice";
import { InvoiceAction } from "@/typings/ui";

/**
 * Composable centralizing non-modal invoice action dispatches.
 * Modal-based actions (approve, accept payment, etc.) are handled
 * by InvoiceActionsContextMenu directly.
 */
export function useInvoiceActions(injectedStore?: Store<any>) {
  const store = injectedStore ?? useStore();

  function download(invoice: Invoice) {
    window.open(invoice.pdf_url);
  }

  async function resumeBatch(invoice: Invoice) {
    try {
      await store.dispatch("updateInvoiceBatching", {
        link: invoice.links?.update_batching,
        exclude_from_batching: false
      });
      store.dispatch("fetchInvoice", invoice.invoice_id);
      window.notify.success("Batching has been resumed for this invoice.");
    } catch {
      window.notify.error(
        "An error occurred when attempting to resume batching."
      );
    }
  }

  async function doNotBatch(invoice: Invoice) {
    await store.dispatch("confirmChanges", {
      title: "Do Not Batch Invoice",
      message:
        "This will disable batching this invoice to your accounting system.<br/> Are you sure you wish to continue?"
    });
    try {
      await store.dispatch("updateInvoiceBatching", {
        link: invoice.links?.update_batching,
        exclude_from_batching: true
      });
      store.dispatch("fetchInvoice", invoice.invoice_id);
      window.notify.success("Batching has been stopped for this invoice.");
    } catch {
      window.notify.error(
        "An error occurred when attempting to stop batching."
      );
    }
  }

  async function perform(invoice: Invoice, action: InvoiceAction) {
    const handlers: Record<string, () => void | Promise<void>> = {
      [InvoiceAction.Download]: () => download(invoice),
      [InvoiceAction.ResumeBatch]: () => resumeBatch(invoice),
      [InvoiceAction.DoNotBatch]: () => doNotBatch(invoice)
    };

    const handler = handlers[action];
    if (handler) {
      await handler();
    }
  }

  return { download, resumeBatch, doNotBatch, perform };
}
